import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
    Box,
    Button,
    Container,
    IconButton,
    InputAdornment,
    Link,
    TextField,
    Toolbar,
    Typography,
} from "@mui/material";

import ChikoLogoAzul from "../static/chiko_logo_azul.png";
import { Visibility, VisibilityOff } from "@mui/icons-material";

// import { useGoogleSignIn } from "../hooks/useGoogleSignIn";
// import GoogleIcon from "../static/google-icon.png";

const LoginPage = () => {
    const backendEndpoint = process.env.REACT_APP_BACKEND_ENDPOINT;

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [validate, setValidate] = useState(false);
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    // const { fetchPopupUrl, completeAuthentication, openPopup, monitorPopup } =
    //     useGoogleSignIn();

    const emailRegex = useMemo(() => /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/, []);

    const navigate = useNavigate();

    const login = useCallback(
        async (email, password) => {
            const response = await fetch(`${backendEndpoint}/login/`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    email: email,
                    password: password,
                }),
            });

            const json = await response.json();
            const authenticated = json.authenticated;
            return authenticated;
        },
        [backendEndpoint],
    );

    const handleLogin = useCallback(async () => {
        setValidate(true);
        if (!emailRegex.test(email) || password === "") {
            setError(true);
            setErrorMessage("");
            return;
        }

        const authenticated = await login(email, password);

        if (authenticated) {
            navigate("/training");
        } else {
            setError(true);
            setErrorMessage("E-mail ou senha incorreto.");
            return;
        }
    }, [navigate, login, emailRegex, email, password]);

    const cleanValidations = () => {
        setValidate(false);
        setError(false);
        setErrorMessage("");
    };

    const getCookieValue = useCallback(
        (name) =>
            document.cookie
                .match("(^|;)\\s*" + name + "\\s*=\\s*([^;]+)")
                ?.pop() || "",
        [],
    );

    const authenticate = useCallback(async () => {
        const response = await fetch(`${backendEndpoint}/authenticate/`, {
            method: "POST",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": getCookieValue("csrfToken"),
            },
        });

        const { authenticated } = await response.json();

        return authenticated;
    }, [backendEndpoint, getCookieValue]);

    useEffect(() => {
        const run = async () => {
            const csrfToken = getCookieValue("csrfToken");

            if (csrfToken) {
                const authenticated = await authenticate();
                if (authenticated) {
                    navigate("/training");
                }
            }
        };
        run();
    }, [getCookieValue, authenticate, navigate]);

    // Função para detectar a tecla Enter
    const handleKeyDown = (event) => {
        if (event.key === "Enter") {
            handleLogin();
        }
    };

    // const handleLoginGoogle = async () => {
    //     const popupUrl = await fetchPopupUrl();

    //     const popup = openPopup(popupUrl, "Google Sign-In");

    //     const code = await monitorPopup(popup);

    //     const authData = await completeAuthentication(code);

    //     if (authData.authenticated) {
    //         navigate("/training");
    //     }
    // };

    return (
        <Container maxWidth="sm">
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    mt: "70px",
                }}
            >
                <Toolbar
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        mt: 2,
                        mb: 5,
                    }}
                >
                    <img src={ChikoLogoAzul} alt="Chiko Logo" height="70" />
                </Toolbar>
                <Typography variant="div" gutterBottom>
                    Acesse com seu Email e Senha
                </Typography>

                <TextField
                    value={email}
                    onChange={(e) => {
                        setEmail(e.target.value);
                        cleanValidations();
                    }}
                    label="E-mail"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    error={validate && !emailRegex.test(email) ? true : false}
                    helperText={
                        validate && !emailRegex.test(email)
                            ? "Preencha com um e-mail válido"
                            : ""
                    }
                />

                <TextField
                    value={password}
                    onChange={(e) => {
                        setPassword(e.target.value);
                        cleanValidations();
                    }}
                    label="Senha"
                    type={showPassword ? "text" : "password"}
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    error={validate && password === "" ? true : false}
                    helperText={
                        validate && password === "" ? "Preencha sua senha" : ""
                    }
                    onKeyDown={handleKeyDown}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={() =>
                                        setShowPassword(!showPassword)
                                    }
                                    edge="end"
                                >
                                    {showPassword ? (
                                        <VisibilityOff />
                                    ) : (
                                        <Visibility />
                                    )}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

                {error ? (
                    <Typography variant="h6">{errorMessage}</Typography>
                ) : null}

                <Link
                    variant="text"
                    sx={{
                        alignSelf: "flex-end",
                        textDecoration: "none",
                        ":hover": { cursor: "pointer" },
                    }}
                    onClick={() => navigate("/forgot-password")}
                >
                    Esqueceu a senha?
                </Link>

                <Button
                    variant="contained"
                    color="primary"
                    sx={{ marginTop: 4, padding: "14px !important" }}
                    fullWidth
                    component={Link}
                    onClick={() => handleLogin()}
                >
                    Entrar
                </Button>
                {/* <Button
                    variant="outlined"
                    onClick={handleLoginGoogle}
                    sx={{
                        width: "100%",
                        marginTop: 2,
                        textTransform: "uppercase",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                    }}
                >
                    <img src={GoogleIcon} alt="google icon" />
                    Continuar com o Google
                </Button> */}

                <Box
                    sx={{
                        color: "#bbb",
                        margin: "20px 0",
                    }}
                >
                    ________________
                </Box>
                <Box>
                    Não tem conta? &nbsp;
                    <Link
                        variant="text"
                        sx={{
                            textDecoration: "none",
                            ":hover": { cursor: "pointer" },
                        }}
                        href="https://wa.me/+5521996367947?text=Olá, quero um trial da plataforma!"
                        target="_blank"
                    >
                        Solicite um trial
                    </Link>
                </Box>

                <Box
                    sx={{
                        mt: "30px",
                        color: "#ccc",
                        fontSize: "12px",
                    }}
                >
                    <Link
                        variant="text"
                        sx={{
                            color: "#aaa",
                            textDecoration: "none",
                            ":hover": { cursor: "pointer" },
                        }}
                        href="https://www.chiko.ai/politica-de-privacidade"
                        target="_blank"
                    >
                        Politica de privacidade
                    </Link>
                    &nbsp;&nbsp;|&nbsp;&nbsp;
                    <Link
                        variant="text"
                        sx={{
                            color: "#aaa",
                            textDecoration: "none",
                            ":hover": { cursor: "pointer" },
                        }}
                        href="https://www.chiko.ai/termos-de-uso"
                        target="_blank"
                    >
                        Termos de uso
                    </Link>
                </Box>
            </Box>
        </Container>
    );
};

export default LoginPage;
